import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client';
import type { ApiClient } from '../api/client';
import type { Relay } from '../api/types';

export const POLL_INTERVAL_MS = 5000;

export function isTransitional(relay: Relay | null): boolean {
  return relay?.state === 'starting' || relay?.state === 'stopping';
}

export function errorText(e: unknown): string {
  return e instanceof ApiError || e instanceof Error ? e.message : 'Something went wrong.';
}

export interface UseRelay {
  relay: Relay | null;
  loading: boolean;
  error: string | null;
  /** Which action is currently in flight (button busy state), if any. */
  pending: 'start' | 'stop' | null;
  refresh: () => Promise<void>;
  start: () => Promise<void>;
  stop: () => Promise<void>;
}

/**
 * Relay state with the polling policy from CONTRACT section 10: fetch on load and after
 * actions; poll every 5 s only while starting/stopping AND the tab is visible. The API wakes
 * from scale-to-zero on every call, so idle polling would cost real money.
 */
export function useRelay(api: ApiClient, enabled: boolean): UseRelay {
  const [relay, setRelay] = useState<Relay | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<'start' | 'stop' | null>(null);
  const inflight = useRef(false);

  const refresh = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      setRelay(await api.getRelay());
      setError(null);
    } catch (e) {
      setError(errorText(e));
    } finally {
      inflight.current = false;
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (enabled) void refresh();
    else setLoading(false);
  }, [enabled, refresh]);

  const transitional = isTransitional(relay);
  useEffect(() => {
    if (!transitional) return;
    const tick = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    const id = window.setInterval(tick, POLL_INTERVAL_MS);
    // Catch up immediately when the user returns to the tab instead of waiting for the next tick.
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [transitional, refresh]);

  const act = useCallback(
    async (kind: 'start' | 'stop') => {
      setPending(kind);
      setError(null);
      try {
        const result = await (kind === 'start' ? api.startRelay() : api.stopRelay());
        setRelay(result.relay);
      } catch (e) {
        setError(errorText(e));
        // relay_busy / conflict mean our view is stale; resync so the buttons reflect reality.
        if (e instanceof ApiError && (e.code === 'relay_busy' || e.code === 'conflict')) {
          inflight.current = false;
          void refresh();
        }
      } finally {
        setPending(null);
      }
    },
    [api, refresh],
  );

  return {
    relay,
    loading,
    error,
    pending,
    refresh,
    start: useCallback(() => act('start'), [act]),
    stop: useCallback(() => act('stop'), [act]),
  };
}
